// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).
function isVisible(element) {
    let current = element;
    const view = element.ownerDocument.defaultView;
    while (current !== null) {
        if (current.hidden || current.getAttribute('aria-hidden') === 'true') {
            return false;
        }
        const style = view?.getComputedStyle(current);
        if (style?.display === 'none' || style?.visibility === 'hidden') {
            return false;
        }
        current = current.parentElement;
    }
    return true;
}
function isCancelButton(element) {
    const accessibleName = (element.getAttribute('aria-label') ??
        element.textContent ??
        '').trim();
    return accessibleName === 'Cancel' && !element.disabled && isVisible(element);
}
function cancelButtons(root) {
    return [...root.querySelectorAll('button')].filter(isCancelButton);
}
function closestButtonToTarget(buttons, target) {
    const targetAncestors = new Map();
    let targetAncestor = target;
    let targetDistance = 0;
    while (targetAncestor !== null) {
        targetAncestors.set(targetAncestor, targetDistance);
        targetAncestor = targetAncestor.parentNode;
        targetDistance += 1;
    }
    let closest = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    let isTied = false;
    for (const button of buttons) {
        let buttonAncestor = button;
        let buttonDistance = 0;
        while (buttonAncestor !== null) {
            const distanceFromTarget = targetAncestors.get(buttonAncestor);
            if (distanceFromTarget !== undefined) {
                const distance = distanceFromTarget + buttonDistance;
                if (distance < closestDistance) {
                    closest = button;
                    closestDistance = distance;
                    isTied = false;
                }
                else if (distance === closestDistance) {
                    isTied = true;
                }
                break;
            }
            buttonAncestor = buttonAncestor.parentNode;
            buttonDistance += 1;
        }
    }
    return isTied ? null : closest;
}
function resolveCancelButton(root, target) {
    const buttons = cancelButtons(root);
    if (buttons.length === 0) {
        return null;
    }
    if (buttons.length === 1) {
        return buttons[0] ?? null;
    }
    const dialogs = [
        ...root.querySelectorAll('[role="dialog"][aria-modal="true"]'),
    ].filter(isVisible);
    const activeDialog = dialogs.at(-1);
    if (activeDialog !== undefined) {
        const dialogButtons = cancelButtons(activeDialog);
        if (dialogButtons.length === 1) {
            return dialogButtons[0] ?? null;
        }
    }
    return target instanceof Node ? closestButtonToTarget(buttons, target) : null;
}
export function handleEscapeCancel(event, root) {
    if (event.key !== 'Escape' ||
        event.defaultPrevented ||
        event.isComposing ||
        event.repeat) {
        return false;
    }
    const button = resolveCancelButton(root, event.target);
    if (button === null) {
        return false;
    }
    event.preventDefault();
    event.stopPropagation();
    button.click();
    return true;
}
export function installEscapeCancel(document) {
    const handleKeyDown = (event) => {
        handleEscapeCancel(event, document);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
}
//# sourceMappingURL=escape-cancel.js.map